import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

const HEADER_FILL = 'FF1F4E78';
const HEADER_FONT_COLOR = 'FFFFFFFF';

export const HIGHLIGHT_HEAT_ILLNESS = 'FFFFCDD2';
export const HIGHLIGHT_WATCH_LIST = 'FFFFF59D';
export const HIGHLIGHT_PREDISPOSED = 'FFFFE0B2';
export const HIGHLIGHT_RISK_CELL = 'FFEF9A9A';
export const HIGHLIGHT_OVER_GOAL_CELL = 'FFFFCCBC';
export const HIGHLIGHT_IN_OUT_WARNING = 'FFFFF59D';
export const HIGHLIGHT_IN_OUT_DANGER = 'FFEF9A9A';
export const HIGHLIGHT_ACHIEVED = 'FFC8E6C9';

const IN_OUT_WARNING_THRESHOLD = 0.03;
const IN_OUT_DANGER_THRESHOLD = 0.05;

export const RISK_TEXT_COLOR = 'FFB71C1C';

export const cleanDataForExport = (data, passcodeByAthleteId = null) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map(item => {
    const cleanedItem = { ...item };

    delete cleanedItem.weightId;
    delete cleanedItem.teamId;
    delete cleanedItem.organizationId;
    delete cleanedItem._orderByCreated;
    delete cleanedItem._orderById;

    if (cleanedItem.athleteId) {
      // The athlete's 4-digit identifier is the passcode they type on the scale.
      // The report endpoints don't return it, so it's looked up by athlete id from
      // the organization athlete list. Kept as a string so leading zeros survive Excel.
      if (passcodeByAthleteId && passcodeByAthleteId[cleanedItem.athleteId] !== undefined) {
        cleanedItem['Passcode'] = String(passcodeByAthleteId[cleanedItem.athleteId]);
      }
      cleanedItem['Athlete ID'] = cleanedItem.athleteId;
      delete cleanedItem.athleteId;
    }

    let athleteNoteValue = null;
    if (cleanedItem.athleteNote) {
      athleteNoteValue = cleanedItem.athleteNote;
      delete cleanedItem.athleteNote;
    }
    if (cleanedItem.note) {
      athleteNoteValue = cleanedItem.note;
      delete cleanedItem.note;
    }
    if (athleteNoteValue) {
      cleanedItem['Athlete Note'] = athleteNoteValue;
    }

    if (cleanedItem.athleteName) {
      cleanedItem['Athlete Name'] = cleanedItem.athleteName;
      const parts = String(cleanedItem.athleteName).trim().split(/\s+/);
      if (parts.length > 1) {
        cleanedItem['Last Name'] = parts[parts.length - 1];
      } else if (parts.length === 1) {
        cleanedItem['Last Name'] = parts[0];
      }
      delete cleanedItem.athleteName;
    }
    if (cleanedItem.teamName) {
      cleanedItem['Team Name'] = cleanedItem.teamName;
      delete cleanedItem.teamName;
    }
    if (cleanedItem.rosterName) {
      cleanedItem['Roster Name'] = cleanedItem.rosterName;
      delete cleanedItem.rosterName;
    }
    if (cleanedItem.weightDate) {
      // weightDate is the weigh-in `created` timestamp (full ISO). Split it into a clean
      // date column plus a separate Weigh Time (HH:mm), mirroring the weighTime branch.
      const m = moment(cleanedItem.weightDate);
      const hasTime = /[T ]\d{1,2}:\d{2}/.test(String(cleanedItem.weightDate));
      cleanedItem['Weight Date'] = m.isValid() ? m.format('MM/DD/YYYY') : cleanedItem.weightDate;
      if (cleanedItem['Weigh Time'] === undefined && m.isValid() && hasTime) {
        cleanedItem['Weigh Time'] = m.format('HH:mm');
      }
      delete cleanedItem.weightDate;
    }
    if (cleanedItem.weighTime) {
      const m = moment(cleanedItem.weighTime);
      cleanedItem['Weigh Time'] = m.isValid() ? m.format('HH:mm') : cleanedItem.weighTime;
      if (cleanedItem['Weight Date'] === undefined) {
        cleanedItem['Weight Date'] = m.isValid() ? m.format('MM/DD/YYYY') : cleanedItem.weighTime;
      }
      delete cleanedItem.weighTime;
    }
    if (cleanedItem.weightTime) {
      const m = moment(cleanedItem.weightTime);
      cleanedItem['Weight Time'] = m.isValid() ? m.format('HH:mm') : cleanedItem.weightTime;
      if (cleanedItem['Weight Date'] === undefined) {
        cleanedItem['Weight Date'] = m.isValid() ? m.format('MM/DD/YYYY') : cleanedItem.weightTime;
      }
      delete cleanedItem.weightTime;
    }
    if (cleanedItem.watchList !== undefined) {
      cleanedItem['Watch List'] = cleanedItem.watchList;
      delete cleanedItem.watchList;
    }
    if (cleanedItem.predisposed !== undefined) {
      cleanedItem['Predisposed'] = cleanedItem.predisposed;
      delete cleanedItem.predisposed;
    }
    if (cleanedItem.percentChange !== undefined) {
      cleanedItem['Percent Change'] = cleanedItem.percentChange;
      delete cleanedItem.percentChange;
    }
    if (cleanedItem.weightChange !== undefined) {
      cleanedItem['Weight Change'] = cleanedItem.weightChange;
      delete cleanedItem.weightChange;
    }
    if (cleanedItem.athleteTotalTrackingDays !== undefined) {
      cleanedItem['Total Tracking Days'] = cleanedItem.athleteTotalTrackingDays;
      delete cleanedItem.athleteTotalTrackingDays;
    }
    if (cleanedItem.percentTracked !== undefined) {
      cleanedItem['Percent Tracked'] = cleanedItem.percentTracked;
      delete cleanedItem.percentTracked;
    }
    if (cleanedItem.totalTrackingDays !== undefined) {
      cleanedItem['Team Total Tracking Days'] = cleanedItem.totalTrackingDays;
      delete cleanedItem.totalTrackingDays;
    }
    if (cleanedItem.graduationYear !== undefined) {
      cleanedItem['Graduation Year'] = cleanedItem.graduationYear;
      delete cleanedItem.graduationYear;
    }
    if (cleanedItem.heatIllness !== undefined) {
      cleanedItem['Heat Illness'] = cleanedItem.heatIllness;
      delete cleanedItem.heatIllness;
    }
    if (cleanedItem.euhydrated !== undefined) {
      cleanedItem['Euhydrated'] = cleanedItem.euhydrated;
      delete cleanedItem.euhydrated;
    }
    if (cleanedItem.predispositionThreshold !== undefined) {
      cleanedItem['Predisposition Threshold'] = cleanedItem.predispositionThreshold;
      delete cleanedItem.predispositionThreshold;
    }
    if (cleanedItem.dehydrationThreshold !== undefined) {
      cleanedItem['Dehydration Threshold'] = cleanedItem.dehydrationThreshold;
      delete cleanedItem.dehydrationThreshold;
    }
    if (cleanedItem.initialWeight !== undefined) {
      cleanedItem['Initial Weight'] = cleanedItem.initialWeight;
      delete cleanedItem.initialWeight;
    }
    if (cleanedItem.targetWeightT !== undefined) {
      cleanedItem['Target Weight'] = cleanedItem.targetWeightT;
      delete cleanedItem.targetWeightT;
    }
    if (cleanedItem.targetCreated) {
      cleanedItem['Target Created'] = cleanedItem.targetCreated;
      delete cleanedItem.targetCreated;
    }
    if (cleanedItem.targetDate) {
      cleanedItem['Target Date'] = cleanedItem.targetDate;
      delete cleanedItem.targetDate;
    }
    if (cleanedItem.wDate) {
      cleanedItem['Date'] = cleanedItem.wDate;
      delete cleanedItem.wDate;
    }
    if (cleanedItem.in2In !== undefined) {
      // Keep the sign: positive = gain, negative = loss. The Excel number format
      // renders the direction as an up/down arrow instead of a minus sign.
      cleanedItem['IN to IN % CHANGE'] = cleanedItem.in2In;
      delete cleanedItem.in2In;
    }
    if (cleanedItem.weight2In !== undefined) {
      cleanedItem['IN to IN LBS CHANGE'] = cleanedItem.weight2In;
      delete cleanedItem.weight2In;
    }
    if (cleanedItem.in2OutPct !== undefined) {
      cleanedItem['IN to OUT % CHANGE'] = cleanedItem.in2OutPct;
      delete cleanedItem.in2OutPct;
    }
    if (cleanedItem.in2OutWeightChange !== undefined) {
      cleanedItem['IN to OUT LBS CHANGE'] = cleanedItem.in2OutWeightChange;
      delete cleanedItem.in2OutWeightChange;
    }
    if (cleanedItem.outOverRisk !== undefined) {
      delete cleanedItem.outOverRisk;
    }
    if (cleanedItem.outOverWeight !== undefined) {
      cleanedItem['Out Over Weight'] = cleanedItem.outOverWeight;
      delete cleanedItem.outOverWeight;
    }
    if (cleanedItem.inOverRisk !== undefined) {
      cleanedItem['In Over Risk'] = cleanedItem.inOverRisk;
      delete cleanedItem.inOverRisk;
    }
    if (cleanedItem.inOverWeight !== undefined) {
      cleanedItem['In Over Weight'] = cleanedItem.inOverWeight;
      delete cleanedItem.inOverWeight;
    }

    if (cleanedItem.height !== undefined) {
      cleanedItem['Height'] = cleanedItem.height;
      delete cleanedItem.height;
    }
    if (cleanedItem.weight !== undefined) {
      cleanedItem['Weight'] = cleanedItem.weight;
      delete cleanedItem.weight;
    }
    if (cleanedItem.bmi !== undefined) {
      cleanedItem['BMI'] = cleanedItem.bmi;
      delete cleanedItem.bmi;
    }
    if (cleanedItem.type !== undefined) {
      cleanedItem['Weight Type'] = cleanedItem.type;
      delete cleanedItem.type;
    }
    if (cleanedItem.gender !== undefined) {
      cleanedItem['Gender'] = cleanedItem.gender;
      delete cleanedItem.gender;
    }
    if (cleanedItem.dob !== undefined) {
      cleanedItem['DOB'] = cleanedItem.dob;
      delete cleanedItem.dob;
    }
    if (cleanedItem.ethnicity !== undefined) {
      cleanedItem['Ethnicity'] = cleanedItem.ethnicity;
      delete cleanedItem.ethnicity;
    }
    if (cleanedItem.achieved !== undefined) {
      cleanedItem['Achieved'] = cleanedItem.achieved;
      delete cleanedItem.achieved;
    }

    const targetWeightValue = Number(cleanedItem['Target Weight']);
    const currentWeightValue = Number(cleanedItem['Weight']);
    if (Number.isFinite(targetWeightValue) && Number.isFinite(currentWeightValue)) {
      cleanedItem['LBS to Goal'] = Number((targetWeightValue - currentWeightValue).toFixed(2));
    }

    const targetDateValue = cleanedItem['Target Date'];
    if (targetDateValue) {
      const m = moment(targetDateValue);
      if (m.isValid()) {
        cleanedItem['Days to Goal'] = m.diff(moment(), 'days');
      }
    }

    return cleanedItem;
  });
};

export const orderColumns = (data, columnsOrder) => {
  return data.map(item => {
    const orderedItem = {};
    columnsOrder.forEach(column => {
      orderedItem[column] = item[column] !== undefined ? item[column] : '';
    });
    return orderedItem;
  });
};

export const getSelectedColumnsOrder = (defaultColumnsOrder, selectedFields, dataType) => {
  if (!selectedFields || !selectedFields[dataType]) {
    return defaultColumnsOrder;
  }

  return defaultColumnsOrder.filter(column => selectedFields[dataType][column]);
};

const isTruthy = value => value === true || value === 'true' || value === 1 || value === '1';

const matchesSports = (item, sports) => {
  if (!sports || sports.length === 0) return true;
  // Some datasets (e.g. weight change) carry teamId but no teamName, so there is
  // nothing to match on — don't drop those rows, otherwise the sheet vanishes.
  if (item.teamName === undefined || item.teamName === null) return true;
  return sports.includes(item.teamName);
};

// Builds a teamId -> teamName lookup from any datasets that expose both fields,
// so datasets that only carry teamId (weight change) can be enriched with a name.
const buildTeamNameById = datasets => {
  const map = {};
  datasets.forEach(dataset => {
    if (!Array.isArray(dataset)) return;
    dataset.forEach(item => {
      if (item && item.teamId && item.teamName && map[item.teamId] === undefined) {
        map[item.teamId] = item.teamName;
      }
    });
  });
  return map;
};

const enrichTeamName = (data, teamNameById) => {
  if (!Array.isArray(data)) return data;
  return data.map(item => {
    if (item && item.teamName === undefined && item.teamId && teamNameById[item.teamId] !== undefined) {
      return { ...item, teamName: teamNameById[item.teamId] };
    }
    return item;
  });
};

const matchesAthletes = (item, athletes) => {
  if (!athletes || athletes.length === 0) return true;
  return athletes.includes(item.athleteName) || athletes.includes(item.name);
};

const matchesDateRange = (rawDate, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  if (!rawDate) return true;
  const itemDate = moment(rawDate);
  if (!itemDate.isValid()) return true;
  return itemDate.isBetween(
    dateFrom ? moment(dateFrom).startOf('day') : moment('1900-01-01'),
    dateTo ? moment(dateTo).endOf('day') : moment('2999-12-31'),
    null,
    '[]'
  );
};

const applyFilters = (data, filters, dateField) => {
  if (!Array.isArray(data)) return [];
  if (!filters) return data;

  const { sports, athletes, dateFrom, dateTo } = filters;

  return data.filter(item => {
    if (!matchesSports(item, sports)) return false;
    if (!matchesAthletes(item, athletes)) return false;
    if (dateField && !matchesDateRange(item[dateField], dateFrom, dateTo)) return false;
    return true;
  });
};

const applyHeaderStyle = row => {
  row.eachCell(cell => {
    cell.font = { bold: true, color: { argb: HEADER_FONT_COLOR } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
      bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
      left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
      right: { style: 'thin', color: { argb: 'FFCCCCCC' } },
    };
  });
};

const fillRow = (row, argb) => {
  row.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
  });
};

const fillCell = (cell, argb) => {
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
};

const setCellRiskFont = cell => {
  cell.font = { color: { argb: RISK_TEXT_COLOR }, bold: true };
};

const autoFitColumns = worksheet => {
  worksheet.columns.forEach(column => {
    let maxLength = 10;
    column.eachCell({ includeEmpties: true }, cell => {
      const value = cell.value === null || cell.value === undefined ? '' : String(cell.value);
      if (value.length > maxLength) maxLength = value.length;
    });
    column.width = Math.min(maxLength + 2, 40);
  });
};

// Renders the static color legend rows Sharon asked for at the top of each sheet:
// a bold title row, then one row per item (swatch cell + label), then a spacer row.
const addSheetLegend = (worksheet, legendItems) => {
  const titleRow = worksheet.addRow(['COLOR LEGEND']);
  titleRow.getCell(1).font = { bold: true };

  legendItems.forEach(item => {
    const row = worksheet.addRow(['', item.label]);
    const swatchCell = row.getCell(1);
    if (item.color) {
      fillCell(swatchCell, item.color);
    }
    if (item.fontColor) {
      swatchCell.value = 'A';
      swatchCell.font = { bold: true, color: { argb: item.fontColor } };
      swatchCell.alignment = { horizontal: 'center' };
    }
    row.getCell(2).font = { bold: true };
  });

  worksheet.addRow([]);
};

const buildSheet = (
  workbook,
  sheetName,
  columnOrder,
  rows,
  applyConditionalFormatting,
  columnFormats = null,
  legendItems = null
) => {
  const worksheet = workbook.addWorksheet(sheetName);

  worksheet.columns = columnOrder.map(name => ({
    key: name,
    width: 18,
    ...(columnFormats && columnFormats[name] ? { style: { numFmt: columnFormats[name] } } : {}),
  }));

  if (legendItems && legendItems.length > 0) {
    addSheetLegend(worksheet, legendItems);
  }

  const headerRow = worksheet.addRow(columnOrder);
  const headerRowNumber = headerRow.number;

  rows.forEach(rowData => {
    const row = worksheet.addRow(rowData);
    if (applyConditionalFormatting) applyConditionalFormatting(row, rowData, columnOrder);
  });

  applyHeaderStyle(headerRow);
  worksheet.views = [{ state: 'frozen', ySplit: headerRowNumber }];
  worksheet.autoFilter = {
    from: { row: headerRowNumber, column: 1 },
    to: { row: headerRowNumber, column: columnOrder.length },
  };
  autoFitColumns(worksheet);
};

const formatBmiRow = (row, rowData, columnOrder) => {
  if (isTruthy(rowData['Predisposed'])) {
    fillRow(row, HIGHLIGHT_PREDISPOSED);
  }

  const colIndex = name => columnOrder.indexOf(name) + 1;

  const predispositionThreshold = Number(rowData['Predisposition Threshold']);

  const in2InIdx = colIndex('IN to IN % CHANGE');
  if (in2InIdx > 0) {
    const cell = row.getCell(in2InIdx);
    const val = Number(cell.value);
    const threshold =
      Number.isFinite(predispositionThreshold) && predispositionThreshold !== 0 ? predispositionThreshold : 0.02;
    if (Number.isFinite(val) && Math.abs(val) >= Math.abs(threshold)) {
      setCellRiskFont(cell);
    }
  }

  const in2OutIdx = colIndex('IN to OUT % CHANGE');
  if (in2OutIdx > 0) {
    const cell = row.getCell(in2OutIdx);
    const val = Number(cell.value);
    if (Number.isFinite(val)) {
      const absVal = Math.abs(val);
      if (absVal >= IN_OUT_DANGER_THRESHOLD) {
        fillCell(cell, HIGHLIGHT_IN_OUT_DANGER);
      } else if (absVal >= IN_OUT_WARNING_THRESHOLD) {
        fillCell(cell, HIGHLIGHT_IN_OUT_WARNING);
      }
    }
  }

  const inOverRiskIdx = colIndex('In Over Risk');
  if (inOverRiskIdx > 0 && isTruthy(rowData['In Over Risk'])) {
    fillCell(row.getCell(inOverRiskIdx), HIGHLIGHT_RISK_CELL);
  }
};

const formatWeightChangeRow = (row, rowData, columnOrder) => {
  if (isTruthy(rowData['Predisposed'])) {
    fillRow(row, HIGHLIGHT_PREDISPOSED);
  }

  const colIndex = name => columnOrder.indexOf(name) + 1;

  const pctIdx = colIndex('Percent Change');
  if (pctIdx > 0) {
    const cell = row.getCell(pctIdx);
    const val = Number(cell.value);
    if (Number.isFinite(val) && Math.abs(val) >= 0.02) {
      setCellRiskFont(cell);
    }
  }
};

const formatTargetWeightRow = (row, rowData, columnOrder) => {
  const colIndex = name => columnOrder.indexOf(name) + 1;

  const targetWeight = Number(rowData['Target Weight']);
  const weight = Number(rowData['Weight']);
  if (Number.isFinite(targetWeight) && Number.isFinite(weight)) {
    const weightCellIdx = colIndex('Weight');
    if (weightCellIdx > 0 && weight > targetWeight) {
      fillCell(row.getCell(weightCellIdx), HIGHLIGHT_OVER_GOAL_CELL);
    }
  }

  if (isTruthy(rowData['Achieved'])) {
    const achievedIdx = colIndex('Achieved');
    if (achievedIdx > 0) {
      fillCell(row.getCell(achievedIdx), HIGHLIGHT_ACHIEVED);
    }
  }
};

const formatWbgtRow = (row, rowData, columnOrder) => {
  const colIndex = name => columnOrder.indexOf(name) + 1;

  const peakIdx = colIndex('peak');
  if (peakIdx > 0) {
    const cell = row.getCell(peakIdx);
    const val = Number(cell.value);
    if (Number.isFinite(val)) {
      if (val >= 92) {
        fillCell(cell, HIGHLIGHT_RISK_CELL);
        setCellRiskFont(cell);
      } else if (val >= 88) {
        fillCell(cell, HIGHLIGHT_WATCH_LIST);
      } else if (val >= 82) {
        fillCell(cell, HIGHLIGHT_PREDISPOSED);
      }
    }
  }
};

export const exportToExcel = async (
  data,
  organizationName,
  selectedFields = null,
  filters = null,
  customFileName = null,
  passcodeByAthleteId = null
) => {
  const { weightChangeData, bmiData, targetWeightData, wbgtData } = data;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'SportScale';
  workbook.created = new Date();

  const bmiColumnsOrder = [
    'Athlete Name',
    'Last Name',
    'Passcode',
    'Team Name',
    'Roster Name',
    'Height',
    // Date, Time, Weight — kept in this order to match the Team Data sheet.
    'Weight Date',
    'Weigh Time',
    'Weight',
    'BMI',
    'Weight Type',
    'Gender',
    'Graduation Year',
    'DOB',
    'Ethnicity',
    'Athlete Note',
    'Watch List',
    'Heat Illness',
    'Euhydrated',
    'Predisposition Threshold',
    'Dehydration Threshold',
    'IN to IN % CHANGE',
    'IN to IN LBS CHANGE',
    'IN to OUT % CHANGE',
    'IN to OUT LBS CHANGE',
    'Out Over Weight',
    'In Over Risk',
    'In Over Weight',
    'Athlete ID',
  ];

  const weightChangeColumnsOrder = [
    'Athlete Name',
    'Last Name',
    'Team Name',
    'Weight Date',
    'Weigh Time',
    'Weight',
    'Weight Type',
    'Predisposed',
    'Percent Change',
    'Weight Change',
    'Total Tracking Days',
    'Percent Tracked',
    'Team Total Tracking Days',
    'Athlete ID',
    'Athlete Note',
  ];

  const targetWeightColumnsOrder = [
    'Athlete Name',
    'Last Name',
    'Team Name',
    'Roster Name',
    'Height',
    'Gender',
    'Graduation Year',
    'DOB',
    'Ethnicity',
    'Watch List',
    'Initial Weight',
    'Target Weight',
    'Weight Date',
    'Weight Time',
    'Weight',
    'LBS to Goal',
    'Days to Goal',
    'Target Created',
    'Target Date',
    'Achieved',
    'Athlete ID',
    'Athlete Note',
  ];

  const wbgtColumnsOrder = ['Team Name', 'name', 'initial', 'peak', 'Date'];

  // Percent-change cells stay numeric (sortable/filterable) while the custom format
  // shows direction as ▲ (gain) / ▼ (loss) instead of a minus sign.
  const pctArrowFormat = '0.0%"▲";0.0%"▼";0.0%';
  const bmiColumnFormats = {
    // Text format so a passcode like "0123" keeps its leading zero.
    Passcode: '@',
    'IN to IN % CHANGE': pctArrowFormat,
    'IN to IN LBS CHANGE': '0.00',
    'IN to OUT % CHANGE': pctArrowFormat,
    'IN to OUT LBS CHANGE': '0.00',
  };

  // Per-sheet color legends (rendered as static rows above the header).
  const teamAndAthleteLegend = [
    { color: HIGHLIGHT_PREDISPOSED, label: 'Predisposed' },
    { color: HIGHLIGHT_IN_OUT_WARNING, label: 'IN to OUT % — Warning (≥ 3%)' },
    { color: HIGHLIGHT_IN_OUT_DANGER, label: 'IN to OUT % — Danger (≥ 5%)' },
    { fontColor: RISK_TEXT_COLOR, label: 'IN to IN % over threshold (red text)' },
  ];
  const targetWeightLegend = [
    { color: HIGHLIGHT_OVER_GOAL_CELL, label: 'Weight Over Goal' },
    { color: HIGHLIGHT_ACHIEVED, label: 'Goal Achieved' },
  ];

  const teamNameById = buildTeamNameById([bmiData, targetWeightData, wbgtData, weightChangeData]);
  const weightChangeWithTeam = enrichTeamName(weightChangeData, teamNameById);

  const filteredWeightChange = applyFilters(weightChangeWithTeam, filters, 'weightDate');
  if (filteredWeightChange.length > 0) {
    const cleaned = cleanDataForExport(filteredWeightChange);
    const cols = getSelectedColumnsOrder(weightChangeColumnsOrder, selectedFields, 'weightChange');
    const ordered = orderColumns(cleaned, cols);
    buildSheet(workbook, 'Team Data', cols, ordered, formatWeightChangeRow, null, teamAndAthleteLegend);
  }

  const filteredBmi = applyFilters(bmiData, filters, 'weighTime');
  if (filteredBmi.length > 0) {
    const cleaned = cleanDataForExport(filteredBmi, passcodeByAthleteId);
    const cols = getSelectedColumnsOrder(bmiColumnsOrder, selectedFields, 'bmi');
    const ordered = orderColumns(cleaned, cols);
    buildSheet(workbook, 'Athlete Data', cols, ordered, formatBmiRow, bmiColumnFormats, teamAndAthleteLegend);
  }

  const filteredTargetWeight = applyFilters(targetWeightData, filters, 'weightTime');
  if (filteredTargetWeight.length > 0) {
    const cleaned = cleanDataForExport(filteredTargetWeight);
    const cols = getSelectedColumnsOrder(targetWeightColumnsOrder, selectedFields, 'targetWeight');
    const ordered = orderColumns(cleaned, cols);
    buildSheet(workbook, 'Target Weight Data', cols, ordered, formatTargetWeightRow, null, targetWeightLegend);
  }

  const filteredWbgt = applyFilters(wbgtData, filters, 'wDate');
  if (filteredWbgt.length > 0) {
    const cleaned = cleanDataForExport(filteredWbgt);
    const cols = getSelectedColumnsOrder(wbgtColumnsOrder, selectedFields, 'wbgt');
    const ordered = orderColumns(cleaned, cols);
    buildSheet(workbook, 'WBGT Data', cols, ordered, formatWbgtRow);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const defaultFileName = `${organizationName}_Athletes_Weight_Report_${moment().format('YYYY-MM-DD')}`;
  const sanitized = String(customFileName || defaultFileName)
    .trim()
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\.xlsx$/i, '');
  const fileName = `${sanitized || defaultFileName}.xlsx`;
  saveAs(blob, fileName);
};
